import type { BorrowMarketRow } from '@/hooks/useBorrowMarkets'
import { searchBorrowMarkets } from '@/utils/borrowMarkets/searchBorrowMarkets'
import { useDeferredValue, useMemo } from 'react'
import type { NumberRangeSearchState, useBorrowMarketsSearchState } from './useBorrowMarketsSearchState'

type FilteredBorrowMarketsProps = {
  borrowMarkets: BorrowMarketRow[]
} & Pick<
  ReturnType<typeof useBorrowMarketsSearchState>,
  | 'searchFor'
  | 'selectedNetworks'
  | 'selectedProtocols'
  | 'selectedTokens'
  | 'selectedCollaterals'
  | 'selectedMarketSizeRange'
  | 'selectedLiquidityRange'
>

export function useFilteredBorrowMarkets({
  borrowMarkets,
  searchFor,
  selectedNetworks,
  selectedProtocols,
  selectedTokens,
  selectedCollaterals,
  selectedMarketSizeRange,
  selectedLiquidityRange,
}: FilteredBorrowMarketsProps) {
  const deferredSearchFor = useDeferredValue(searchFor)

  const filteredBorrowMarkets = useMemo(
    () =>
      borrowMarkets.filter((borrowMarket) => {
        const isInSelectedNetwork = selectedNetworks.length === 0 || selectedNetworks.includes(borrowMarket.networkName)
        const isInSelectedProtocol =
          selectedProtocols.length === 0 ||
          selectedProtocols.some((protocol) => protocol.toLowerCase() === borrowMarket.protocolName.toLowerCase())
        const isInSelectedToken =
          selectedTokens.length === 0 ||
          selectedTokens.some((token) => token.toLowerCase() === borrowMarket.tokenSymbol.toLowerCase())
        const isInSelectedCollateral =
          selectedCollaterals.length === 0 ||
          selectedCollaterals.some((collateral) =>
            borrowMarket.collateralAssets.some(
              (assetData) => assetData.asset.symbol.toLowerCase() === collateral.toLowerCase()
            )
          )
        const isInSelectedMarketSizeRange = isNumberInOptionalRange(borrowMarket.marketSizeUsd, selectedMarketSizeRange)
        const isInSelectedLiquidityRange = isNumberInOptionalRange(
          borrowMarket.availableToBorrowUsd,
          selectedLiquidityRange
        )

        return (
          isInSelectedNetwork &&
          isInSelectedProtocol &&
          isInSelectedToken &&
          isInSelectedCollateral &&
          isInSelectedMarketSizeRange &&
          isInSelectedLiquidityRange
        )
      }),
    [
      borrowMarkets,
      selectedNetworks,
      selectedProtocols,
      selectedTokens,
      selectedCollaterals,
      selectedMarketSizeRange.from,
      selectedMarketSizeRange.to,
      selectedLiquidityRange.from,
      selectedLiquidityRange.to,
    ]
  )

  return useMemo(() => {
    if (!deferredSearchFor) return filteredBorrowMarkets
    return searchBorrowMarkets(filteredBorrowMarkets, deferredSearchFor)
  }, [filteredBorrowMarkets, deferredSearchFor])
}

const isNumberInOptionalRange = (value: number | undefined, range: NumberRangeSearchState) => {
  if (range.from === undefined && range.to === undefined) {
    return true
  }

  if (value === undefined) {
    return false
  }

  return (range.from === undefined || value >= range.from) && (range.to === undefined || value <= range.to)
}
